import React, { useState, useEffect } from 'react';
import {
  Monitor, Plus, Search, X, ChevronDown, ChevronRight, Edit, Trash2,
  Wrench, Calendar, MapPin, Tag, FileText, Package, Upload, Download, Building, CheckCircle
} from 'lucide-react';
import { getEquipment, getEquipmentMultiStructures, getEquipmentCategories, createEquipment, updateEquipment, deleteEquipment, createEquipmentRepair, completeEquipmentRepair, uploadEquipmentFile, deleteEquipmentFile } from '../api';
import { useStructure } from '../contexts/StructureContext';
import { useToast } from '../contexts/NotificationContext';
import LoadingSpinner from '../components/LoadingSpinner';
import WarrantyBadge from '../components/equipment/WarrantyBadge';

const CATEGORY_ICONS = {
  'UtensilsCrossed': '🍳',
  'Armchair': '🪑',
  'ShieldCheck': '🛡️',
  'Sparkles': '✨',
  'Monitor': '💻',
  'ToyBrick': '🧸',
};

const STATUS_LABELS = {
  active: { label: 'Actif', color: 'text-green-600 bg-green-50' },
  in_repair: { label: 'En réparation', color: 'text-orange-600 bg-orange-50' },
  decommissioned: { label: 'Hors service', color: 'text-gray-500 bg-gray-100' },
};

const EquipmentPage = ({ showAllStructures = false, navPayload, onNavPayloadConsumed }) => {
  const { currentStructure, structures } = useStructure();
  const toast = useToast();
  const [equipment, setEquipment] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchText, setSearchText] = useState('');
  const [filterCategory, setFilterCategory] = useState('all');
  const [filterStatus, setFilterStatus] = useState('all');
  const [collapsedCategories, setCollapsedCategories] = useState({});
  const [collapsedStructures, setCollapsedStructures] = useState({});
  const [showAddForm, setShowAddForm] = useState(false);
  const [editingEquipment, setEditingEquipment] = useState(null);
  const [selectedEquipment, setSelectedEquipment] = useState(null);
  const [showRepairForm, setShowRepairForm] = useState(false);
  const [saving, setSaving] = useState(false);

  const [form, setForm] = useState({
    name: '', brand: '', model: '', serialNumber: '',
    purchaseDate: '', warrantyMonths: '', categoryId: '',
    location: '', notes: '', structureId: '',
  });

  const [repairForm, setRepairForm] = useState({
    date: new Date().toISOString().split('T')[0],
    description: '', cost: '', technician: '', company: '',
  });

  const isMulti = showAllStructures && structures.length >= 2;

  useEffect(() => {
    if (currentStructure) {
      fetchData();
    }
  }, [currentStructure, showAllStructures]);

  // Auto-ouverture fiche équipement depuis recherche globale (navPayload.equipmentId)
  useEffect(() => {
    if (!navPayload?.equipmentId || equipment.length === 0) return;
    const eq = equipment.find(e => e.id === navPayload.equipmentId);
    if (eq) {
      setSelectedEquipment(eq);
      onNavPayloadConsumed?.();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [navPayload, equipment]);

  const fetchData = async () => {
    try {
      setLoading(true);
      const structureIds = structures.map(s => s.id);
      const [eqRes, catRes] = await Promise.all([
        isMulti
          ? getEquipmentMultiStructures(structureIds)
          : getEquipment(currentStructure.id),
        getEquipmentCategories(),
      ]);
      setEquipment(eqRes.data);
      setCategories(catRes.data);
    } catch (error) {
      console.error('Error fetching equipment:', error);
      toast.error('Erreur lors du chargement des equipements');
    } finally {
      setLoading(false);
    }
  };

  const resetForm = () => {
    setForm({
      name: '', brand: '', model: '', serialNumber: '',
      purchaseDate: '', warrantyMonths: '', categoryId: '',
      location: '', notes: '', structureId: '',
    });
    setEditingEquipment(null);
    setShowAddForm(false);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.name.trim()) return;

    try {
      setSaving(true);
      const data = {
        ...form,
        structureId: form.structureId || currentStructure.id,
        warrantyMonths: form.warrantyMonths ? parseInt(form.warrantyMonths) : null,
        categoryId: form.categoryId || null,
      };

      if (editingEquipment) {
        await updateEquipment(editingEquipment.id, data);
        toast.success('Equipement modifie');
      } else {
        await createEquipment(data);
        toast.success('Equipement ajoute');
      }
      resetForm();
      fetchData();
    } catch (error) {
      console.error('Error saving equipment:', error);
      toast.error('Erreur lors de l\'enregistrement de l\'equipement');
    } finally {
      setSaving(false);
    }
  };

  const handleEdit = (eq) => {
    setForm({
      name: eq.name || '',
      brand: eq.brand || '',
      model: eq.model || '',
      serialNumber: eq.serialNumber || '',
      purchaseDate: eq.purchaseDate ? eq.purchaseDate.split('T')[0] : '',
      warrantyMonths: eq.warrantyMonths || '',
      categoryId: eq.categoryId || '',
      location: eq.location || '',
      notes: eq.notes || '',
      structureId: eq.structureId || '',
    });
    setEditingEquipment(eq);
    setShowAddForm(true);
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Supprimer cet équipement ?')) return;
    try {
      await deleteEquipment(id);
      if (selectedEquipment?.id === id) setSelectedEquipment(null);
      toast.success('Equipement supprime');
      fetchData();
    } catch (error) {
      console.error('Error deleting equipment:', error);
      toast.error('Erreur lors de la suppression de l\'equipement');
    }
  };

  const handleAddRepair = async (e) => {
    e.preventDefault();
    if (!repairForm.description.trim()) return;

    try {
      await createEquipmentRepair(selectedEquipment.id, {
        ...repairForm,
        cost: repairForm.cost ? parseFloat(repairForm.cost) : null,
      });
      setRepairForm({
        date: new Date().toISOString().split('T')[0],
        description: '', cost: '', technician: '', company: '',
      });
      setShowRepairForm(false);
      toast.success('Reparation ajoutee');
      fetchData();
    } catch (error) {
      console.error('Error creating repair:', error);
      toast.error('Erreur lors de l\'ajout de la reparation');
    }
  };

  const handleCompleteRepair = async (repairId) => {
    if (!window.confirm('Marquer cette réparation comme terminée ?')) return;
    try {
      await completeEquipmentRepair(selectedEquipment.id, repairId);
      toast.success('Reparation terminee');
      fetchData();
    } catch (error) {
      console.error('Error completing repair:', error);
      toast.error('Erreur lors de la mise a jour de la reparation');
    }
  };

  const toggleCategory = (catId) => {
    setCollapsedCategories((prev) => ({ ...prev, [catId]: !prev[catId] }));
  };

  const toggleStructure = (name) => {
    setCollapsedStructures((prev) => ({ ...prev, [name]: !prev[name] }));
  };

  // Filter and group
  const filtered = equipment.filter((eq) => {
    if (filterCategory !== 'all' && eq.categoryId !== filterCategory) return false;
    if (filterStatus !== 'all' && eq.status !== filterStatus) return false;
    if (searchText) {
      const s = searchText.toLowerCase();
      return (
        eq.name.toLowerCase().includes(s) ||
        (eq.brand && eq.brand.toLowerCase().includes(s)) ||
        (eq.serialNumber && eq.serialNumber.toLowerCase().includes(s))
      );
    }
    return true;
  });

  // In multi mode: group by structure first, then by category within each structure
  const groupedByStructure = isMulti ? {} : null;
  if (isMulti) {
    filtered.forEach((eq) => {
      const structName = eq.structureName || 'Structure inconnue';
      const catName = eq.category?.name || 'Sans catégorie';
      if (!groupedByStructure[structName]) groupedByStructure[structName] = {};
      if (!groupedByStructure[structName][catName]) groupedByStructure[structName][catName] = [];
      groupedByStructure[structName][catName].push(eq);
    });
  }

  const groupedByCategory = isMulti ? null : {};
  if (!isMulti) {
    filtered.forEach((eq) => {
      const catName = eq.category?.name || 'Sans catégorie';
      if (!groupedByCategory[catName]) groupedByCategory[catName] = [];
      groupedByCategory[catName].push(eq);
    });
  }

  if (!currentStructure) {
    return (
      <div className="p-8 text-center text-gray-500">
        Sélectionnez une structure pour voir les équipements.
      </div>
    );
  }

  return (
    <div className="container mx-auto p-4">
      {/* Header */}
      <div className="flex items-center gap-3 mb-4">
        <Monitor className="text-primary" size={24} />
        <h2 className="text-xl font-bold text-gray-800">Équipements</h2>
        <span className="bg-primary/10 text-primary text-xs font-semibold px-2 py-0.5 rounded-full">
          {equipment.length}
        </span>
      </div>

      {/* Add/Edit form */}
      {showAddForm && (
        <form onSubmit={handleSubmit} className="bg-white rounded-lg shadow-md p-4 mb-4 border border-primary/20">
          <h3 className="font-semibold text-gray-800 mb-3">
            {editingEquipment ? 'Modifier l\'équipement' : 'Ajouter un équipement'}
          </h3>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
            {isMulti && (
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Structure *</label>
                <select value={form.structureId} onChange={(e) => setForm({ ...form, structureId: e.target.value })} required
                  className="w-full border border-gray-300 rounded-lg py-2 px-3 text-sm focus:ring-2 focus:ring-primary/30 focus:border-primary outline-none">
                  <option value="">Sélectionner une structure</option>
                  {structures.map((s) => (
                    <option key={s.id} value={s.id}>{s.name}</option>
                  ))}
                </select>
              </div>
            )}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Nom *</label>
              <input type="text" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} required
                className="w-full border border-gray-300 rounded-lg py-2 px-3 text-sm focus:ring-2 focus:ring-primary/30 focus:border-primary outline-none" placeholder="Ex: Réfrigérateur cuisine" />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Marque</label>
              <input type="text" value={form.brand} onChange={(e) => setForm({ ...form, brand: e.target.value })}
                className="w-full border border-gray-300 rounded-lg py-2 px-3 text-sm focus:ring-2 focus:ring-primary/30 focus:border-primary outline-none" placeholder="Ex: Bosch" />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Modèle</label>
              <input type="text" value={form.model} onChange={(e) => setForm({ ...form, model: e.target.value })}
                className="w-full border border-gray-300 rounded-lg py-2 px-3 text-sm focus:ring-2 focus:ring-primary/30 focus:border-primary outline-none" />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">N° de série</label>
              <input type="text" value={form.serialNumber} onChange={(e) => setForm({ ...form, serialNumber: e.target.value })}
                className="w-full border border-gray-300 rounded-lg py-2 px-3 text-sm font-mono focus:ring-2 focus:ring-primary/30 focus:border-primary outline-none" />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Catégorie</label>
              <select value={form.categoryId} onChange={(e) => setForm({ ...form, categoryId: e.target.value })}
                className="w-full border border-gray-300 rounded-lg py-2 px-3 text-sm focus:ring-2 focus:ring-primary/30 focus:border-primary outline-none">
                <option value="">Sans catégorie</option>
                {categories.map((c) => (
                  <option key={c.id} value={c.id}>{c.name}</option>
                ))}
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Emplacement</label>
              <input type="text" value={form.location} onChange={(e) => setForm({ ...form, location: e.target.value })}
                className="w-full border border-gray-300 rounded-lg py-2 px-3 text-sm focus:ring-2 focus:ring-primary/30 focus:border-primary outline-none" placeholder="Ex: Cuisine" />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Date d'achat</label>
              <input type="date" value={form.purchaseDate} onChange={(e) => setForm({ ...form, purchaseDate: e.target.value })}
                className="w-full border border-gray-300 rounded-lg py-2 px-3 text-sm focus:ring-2 focus:ring-primary/30 focus:border-primary outline-none" />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Garantie (mois)</label>
              <input type="number" min="0" value={form.warrantyMonths} onChange={(e) => setForm({ ...form, warrantyMonths: e.target.value })}
                className="w-full border border-gray-300 rounded-lg py-2 px-3 text-sm focus:ring-2 focus:ring-primary/30 focus:border-primary outline-none" placeholder="Ex: 24" />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Notes</label>
              <input type="text" value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })}
                className="w-full border border-gray-300 rounded-lg py-2 px-3 text-sm focus:ring-2 focus:ring-primary/30 focus:border-primary outline-none" />
            </div>
          </div>
          <div className="flex items-center gap-2 mt-4">
            <button type="submit" disabled={saving} className="bg-primary text-white px-6 py-2 rounded-lg hover:bg-primary-dark transition font-medium text-sm disabled:opacity-50 disabled:cursor-not-allowed">
              {saving ? 'Enregistrement...' : (editingEquipment ? 'Enregistrer' : 'Ajouter')}
            </button>
            <button type="button" onClick={resetForm} className="text-gray-500 hover:text-gray-700 px-4 py-2 text-sm">
              Annuler
            </button>
          </div>
        </form>
      )}

      {/* Filters */}
      <div className="flex flex-col sm:flex-row gap-3 mb-4">
        <div className="relative flex-1">
          <Search size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
          <input type="text" placeholder="Rechercher par nom, marque, n° de série..."
            value={searchText} onChange={(e) => setSearchText(e.target.value)}
            className="w-full border border-gray-300 rounded-lg py-2.5 pl-10 pr-9 text-sm focus:ring-2 focus:ring-primary/30 focus:border-primary outline-none bg-white" />
          {searchText && (
            <button onClick={() => setSearchText('')} className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600">
              <X size={16} />
            </button>
          )}
        </div>
        <select value={filterCategory} onChange={(e) => setFilterCategory(e.target.value)}
          className="border border-gray-300 rounded-lg py-2.5 px-3 text-sm bg-white focus:ring-2 focus:ring-primary/30 focus:border-primary outline-none min-w-[160px]">
          <option value="all">Toutes les catégories</option>
          {categories.map((c) => (
            <option key={c.id} value={c.id}>{c.name}</option>
          ))}
        </select>
        <select value={filterStatus} onChange={(e) => setFilterStatus(e.target.value)}
          className="border border-gray-300 rounded-lg py-2.5 px-3 text-sm bg-white focus:ring-2 focus:ring-primary/30 focus:border-primary outline-none min-w-[140px]">
          <option value="all">Tous les statuts</option>
          <option value="active">Actif</option>
          <option value="in_repair">En réparation</option>
          <option value="decommissioned">Hors service</option>
        </select>
      </div>

      {/* Equipment list */}
      {loading ? (
        <div className="py-12"><LoadingSpinner size="large" text="Chargement..." /></div>
      ) : filtered.length === 0 ? (
        <div className="bg-white rounded-lg shadow-md p-8 text-center text-gray-500">
          <Monitor size={48} className="mx-auto mb-3 text-gray-300" />
          <p>Aucun équipement enregistré</p>
          <p className="text-sm mt-1">Cliquez sur "Nouvel équipement" pour commencer.</p>
        </div>
      ) : isMulti && groupedByStructure ? (
        /* Mode multi-structures : structure header > catégories */
        <div className="space-y-4">
          {Object.keys(groupedByStructure).sort((a, b) => a.localeCompare(b)).map(structName => {
            const catMap = groupedByStructure[structName];
            const isStructCollapsed = collapsedStructures[structName];
            const structTotal = Object.values(catMap).reduce((sum, arr) => sum + arr.length, 0);
            return (
              <div key={structName}>
                <button
                  onClick={() => toggleStructure(structName)}
                  className="w-full flex items-center justify-between px-4 py-3 bg-primary/10 hover:bg-primary/15 rounded-t-lg transition-colors border-l-4 border-primary"
                >
                  <div className="flex items-center gap-2">
                    {isStructCollapsed ? <ChevronRight size={20} className="text-primary" /> : <ChevronDown size={20} className="text-primary" />}
                    <Building size={18} className="text-primary" />
                    <span className="font-bold text-gray-900">{structName}</span>
                    <span className="text-sm text-gray-500">({structTotal} équipement{structTotal !== 1 ? 's' : ''})</span>
                  </div>
                  <span className="text-xs font-medium px-2 py-0.5 rounded-full bg-primary/10 text-primary">
                    {Object.keys(catMap).length} catégorie{Object.keys(catMap).length !== 1 ? 's' : ''}
                  </span>
                </button>
                {!isStructCollapsed && (
                  <div className="bg-white rounded-b-lg shadow-md overflow-hidden space-y-0 divide-y divide-gray-100">
                    {Object.entries(catMap).map(([catName, items]) => {
                      const catKey = `${structName}_${catName}`;
                      return (
                        <div key={catKey}>
                          <button
                            onClick={() => toggleCategory(catKey)}
                            className="w-full flex items-center justify-between px-4 py-2.5 bg-gray-50 hover:bg-gray-100 transition"
                          >
                            <div className="flex items-center gap-2">
                              {collapsedCategories[catKey] ? <ChevronRight size={16} /> : <ChevronDown size={16} />}
                              <Tag size={14} className="text-primary" />
                              <span className="font-semibold text-gray-700 text-sm">{catName}</span>
                              <span className="text-xs text-gray-400">({items.length})</span>
                            </div>
                          </button>
                          {!collapsedCategories[catKey] && (
                            <div className="divide-y divide-gray-50">
                              {items.map((eq) => {
                                const statusInfo = STATUS_LABELS[eq.status] || STATUS_LABELS.active;
                                return (
                                  <div key={eq.id} className="flex items-center gap-3 px-4 py-3 hover:bg-gray-50 transition">
                                    <div className="flex-1 min-w-0">
                                      <div className="flex items-center gap-2">
                                        <span className="font-medium text-gray-800 truncate">{eq.name}</span>
                                        <span className={`text-xs font-medium px-2 py-0.5 rounded-full ${statusInfo.color}`}>
                                          {statusInfo.label}
                                        </span>
                                      </div>
                                      <div className="flex items-center gap-3 mt-1 text-xs text-gray-400">
                                        {eq.brand && <span>{eq.brand} {eq.model || ''}</span>}
                                        {eq.serialNumber && <span className="font-mono">SN: {eq.serialNumber}</span>}
                                        {eq.location && (
                                          <span className="flex items-center gap-1">
                                            <MapPin size={10} />
                                            {eq.location}
                                          </span>
                                        )}
                                      </div>
                                    </div>
                                    <WarrantyBadge warrantyExpiry={eq.warrantyExpiry} />
                                    <div className="flex items-center gap-1 flex-shrink-0">
                                      <button onClick={() => setSelectedEquipment(eq)} className="p-1.5 text-gray-400 hover:text-primary transition" title="Détails">
                                        <FileText size={16} />
                                      </button>
                                      <button onClick={() => handleEdit(eq)} className="p-1.5 text-gray-400 hover:text-blue-500 transition" title="Modifier">
                                        <Edit size={16} />
                                      </button>
                                      <button onClick={() => handleDelete(eq.id)} className="p-1.5 text-gray-400 hover:text-red-500 transition" title="Supprimer">
                                        <Trash2 size={16} />
                                      </button>
                                    </div>
                                  </div>
                                );
                              })}
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      ) : groupedByCategory ? (
        /* Mode mono-structure : catégories directement */
        <div className="space-y-3">
          {Object.entries(groupedByCategory).map(([catName, items]) => (
            <div key={catName} className="bg-white rounded-lg shadow-md overflow-hidden">
              <button
                onClick={() => toggleCategory(catName)}
                className="w-full flex items-center justify-between p-3 bg-gray-50 hover:bg-gray-100 transition"
              >
                <div className="flex items-center gap-2">
                  {collapsedCategories[catName] ? <ChevronRight size={18} /> : <ChevronDown size={18} />}
                  <Tag size={16} className="text-primary" />
                  <span className="font-semibold text-gray-700">{catName}</span>
                  <span className="text-xs text-gray-400">({items.length})</span>
                </div>
              </button>

              {!collapsedCategories[catName] && (
                <div className="divide-y divide-gray-50">
                  {items.map((eq) => {
                    const statusInfo = STATUS_LABELS[eq.status] || STATUS_LABELS.active;
                    return (
                      <div key={eq.id} className="flex items-center gap-3 px-4 py-3 hover:bg-gray-50 transition">
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2">
                            <span className="font-medium text-gray-800 truncate">{eq.name}</span>
                            <span className={`text-xs font-medium px-2 py-0.5 rounded-full ${statusInfo.color}`}>
                              {statusInfo.label}
                            </span>
                          </div>
                          <div className="flex items-center gap-3 mt-1 text-xs text-gray-400">
                            {eq.brand && <span>{eq.brand} {eq.model || ''}</span>}
                            {eq.serialNumber && <span className="font-mono">SN: {eq.serialNumber}</span>}
                            {eq.location && (
                              <span className="flex items-center gap-1">
                                <MapPin size={10} />
                                {eq.location}
                              </span>
                            )}
                          </div>
                        </div>
                        <WarrantyBadge warrantyExpiry={eq.warrantyExpiry} />
                        <div className="flex items-center gap-1 flex-shrink-0">
                          <button onClick={() => setSelectedEquipment(eq)} className="p-1.5 text-gray-400 hover:text-primary transition" title="Détails">
                            <FileText size={16} />
                          </button>
                          <button onClick={() => handleEdit(eq)} className="p-1.5 text-gray-400 hover:text-blue-500 transition" title="Modifier">
                            <Edit size={16} />
                          </button>
                          <button onClick={() => handleDelete(eq.id)} className="p-1.5 text-gray-400 hover:text-red-500 transition" title="Supprimer">
                            <Trash2 size={16} />
                          </button>
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          ))}
        </div>
      ) : null}

      {/* Equipment detail panel */}
      {selectedEquipment && (
        <>
          <div className="fixed inset-0 bg-black/30 z-40" onClick={() => setSelectedEquipment(null)} />
          <div className="fixed right-0 top-0 bottom-0 w-full max-w-md bg-white shadow-xl z-50 overflow-y-auto">
            <div className="p-4 border-b border-gray-200 flex items-center justify-between">
              <h3 className="font-bold text-gray-800">{selectedEquipment.name}</h3>
              <button onClick={() => setSelectedEquipment(null)} className="p-1 text-gray-400 hover:text-gray-600">
                <X size={20} />
              </button>
            </div>
            <div className="p-4 space-y-4">
              {/* Info */}
              <div className="space-y-2 text-sm">
                {selectedEquipment.brand && <p><span className="text-gray-500">Marque :</span> {selectedEquipment.brand}</p>}
                {selectedEquipment.model && <p><span className="text-gray-500">Modèle :</span> {selectedEquipment.model}</p>}
                {selectedEquipment.serialNumber && <p><span className="text-gray-500">N° de série :</span> <span className="font-mono">{selectedEquipment.serialNumber}</span></p>}
                {selectedEquipment.location && <p><span className="text-gray-500">Emplacement :</span> {selectedEquipment.location}</p>}
                {selectedEquipment.purchaseDate && <p><span className="text-gray-500">Date d'achat :</span> {new Date(selectedEquipment.purchaseDate).toLocaleDateString('fr-FR')}</p>}
                <div className="flex items-center gap-2">
                  <span className="text-gray-500">Garantie :</span>
                  <WarrantyBadge warrantyExpiry={selectedEquipment.warrantyExpiry} />
                  {selectedEquipment.warrantyExpiry && (
                    <span className="text-xs text-gray-400">
                      (jusqu'au {new Date(selectedEquipment.warrantyExpiry).toLocaleDateString('fr-FR')})
                    </span>
                  )}
                </div>
                {selectedEquipment.notes && <p><span className="text-gray-500">Notes :</span> {selectedEquipment.notes}</p>}
              </div>

              {/* Documents */}
              <div className="border-t pt-4">
                <h4 className="font-semibold text-gray-700 flex items-center gap-2 mb-3">
                  <FileText size={16} />
                  Documents
                </h4>
                <div className="space-y-2">
                  {[
                    { type: 'invoice', label: 'Facture', url: selectedEquipment.invoiceUrl, name: selectedEquipment.invoiceName },
                    { type: 'manual', label: 'Notice', url: selectedEquipment.manualUrl, name: selectedEquipment.manualName },
                  ].map(({ type, label, url, name }) => (
                    <div key={type} className="flex items-center justify-between bg-gray-50 rounded-lg px-3 py-2">
                      <span className="text-sm text-gray-600">{label}</span>
                      {url ? (
                        <div className="flex items-center gap-2">
                          <a href={url} target="_blank" rel="noopener noreferrer"
                            className="flex items-center gap-1 text-xs text-primary hover:underline font-medium">
                            <Download size={14} />
                            {name || 'Télécharger'}
                          </a>
                          <button
                            onClick={async () => {
                              try {
                                await deleteEquipmentFile(selectedEquipment.id, type);
                                fetchData();
                                setSelectedEquipment(prev => ({ ...prev, [`${type}Url`]: null, [`${type}Name`]: null }));
                                toast.success('Document supprime');
                              } catch (err) {
                                console.error('Error deleting file:', err);
                                toast.error('Erreur lors de la suppression du document');
                              }
                            }}
                            className="p-1 text-red-400 hover:text-red-600 transition" title="Supprimer"
                          >
                            <Trash2 size={14} />
                          </button>
                        </div>
                      ) : (
                        <label className="flex items-center gap-1 text-xs text-primary cursor-pointer hover:underline font-medium">
                          <Upload size={14} />
                          Ajouter
                          <input type="file" className="hidden" accept=".pdf,.jpg,.jpeg,.png"
                            onChange={async (e) => {
                              if (!e.target.files[0]) return;
                              try {
                                const res = await uploadEquipmentFile(selectedEquipment.id, type, e.target.files[0]);
                                fetchData();
                                setSelectedEquipment(prev => ({
                                  ...prev,
                                  [`${type === 'invoice' ? 'invoice' : 'manual'}Url`]: res.data.invoiceUrl || res.data.manualUrl,
                                  [`${type === 'invoice' ? 'invoice' : 'manual'}Name`]: res.data.invoiceName || res.data.manualName,
                                }));
                                toast.success('Document ajoute');
                              } catch (err) {
                                console.error('Error uploading file:', err);
                                toast.error('Erreur lors de l\'upload du document');
                              }
                            }}
                          />
                        </label>
                      )}
                    </div>
                  ))}
                </div>
              </div>

              {/* Repairs */}
              <div className="border-t pt-4">
                <div className="flex items-center justify-between mb-3">
                  <h4 className="font-semibold text-gray-700 flex items-center gap-2">
                    <Wrench size={16} />
                    Réparations ({selectedEquipment.repairs?.length || 0})
                  </h4>
                  <button
                    onClick={() => setShowRepairForm(!showRepairForm)}
                    className="text-xs bg-primary/10 text-primary px-3 py-1 rounded-full hover:bg-primary/20 transition font-medium"
                  >
                    + Ajouter
                  </button>
                </div>

                {showRepairForm && (
                  <form onSubmit={handleAddRepair} className="bg-gray-50 rounded-lg p-3 mb-3 space-y-2">
                    <input type="date" value={repairForm.date} onChange={(e) => setRepairForm({ ...repairForm, date: e.target.value })} required
                      className="w-full border border-gray-300 rounded py-1.5 px-2 text-sm" />
                    <input type="text" placeholder="Description *" value={repairForm.description} onChange={(e) => setRepairForm({ ...repairForm, description: e.target.value })} required
                      className="w-full border border-gray-300 rounded py-1.5 px-2 text-sm" />
                    <div className="grid grid-cols-2 gap-2">
                      <input type="number" step="0.01" placeholder="Coût (€)" value={repairForm.cost} onChange={(e) => setRepairForm({ ...repairForm, cost: e.target.value })}
                        className="border border-gray-300 rounded py-1.5 px-2 text-sm" />
                      <input type="text" placeholder="Technicien" value={repairForm.technician} onChange={(e) => setRepairForm({ ...repairForm, technician: e.target.value })}
                        className="border border-gray-300 rounded py-1.5 px-2 text-sm" />
                    </div>
                    <input type="text" placeholder="Entreprise" value={repairForm.company} onChange={(e) => setRepairForm({ ...repairForm, company: e.target.value })}
                      className="w-full border border-gray-300 rounded py-1.5 px-2 text-sm" />
                    <div className="flex gap-2">
                      <button type="submit" className="bg-primary text-white px-4 py-1.5 rounded text-sm hover:bg-primary-dark transition">Enregistrer</button>
                      <button type="button" onClick={() => setShowRepairForm(false)} className="text-gray-500 text-sm">Annuler</button>
                    </div>
                  </form>
                )}

                {selectedEquipment.repairs?.length > 0 ? (
                  <div className="space-y-2">
                    {selectedEquipment.repairs.map((r) => (
                      <div key={r.id} className={`rounded-lg p-3 text-sm ${r.completedAt ? 'bg-green-50' : 'bg-gray-50'}`}>
                        <div className="flex items-center justify-between">
                          <span className="font-medium">{r.description}</span>
                          <div className="flex items-center gap-2">
                            <span className="text-xs text-gray-400">{new Date(r.date).toLocaleDateString('fr-FR')}</span>
                            {r.completedAt ? (
                              <span className="flex items-center gap-1 text-xs text-green-600 font-medium">
                                <CheckCircle size={13} />Terminée
                              </span>
                            ) : (
                              <button
                                onClick={() => handleCompleteRepair(r.id)}
                                className="flex items-center gap-1 text-xs text-primary hover:text-primary-dark font-medium"
                                title="Marquer comme terminée"
                              >
                                <CheckCircle size={13} />Terminer
                              </button>
                            )}
                          </div>
                        </div>
                        <div className="flex items-center gap-3 mt-1 text-xs text-gray-400">
                          {r.cost && <span>{parseFloat(r.cost).toFixed(2)}€</span>}
                          {r.technician && <span>{r.technician}</span>}
                          {r.company && <span>{r.company}</span>}
                          {r.completedAt && <span>Terminée le {new Date(r.completedAt).toLocaleDateString('fr-FR')}</span>}
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <p className="text-sm text-gray-400">Aucune réparation enregistrée</p>
                )}
              </div>
            </div>
          </div>
        </>
      )}

      {/* FAB — Nouvel équipement */}
      <button
        onClick={() => { resetForm(); setShowAddForm(true); }}
        className="fixed bottom-6 right-6 z-50 flex items-center justify-center w-14 h-14 bg-primary text-white rounded-full shadow-lg hover:bg-primary-dark transition-all hover:scale-110"
        title="Nouvel équipement"
      >
        <Plus size={26} />
      </button>
    </div>
  );
};

export default EquipmentPage;
